import React, { useState, useEffect, useRef } from "react";
import {
  Mail,
  ChevronDown,
  Loader2,
  CheckCircle,
  Truck,
  Clock,
  RotateCcw,
  MapPin,
  Calendar,
  ExternalLink
} from "lucide-react";
import { fetchContactMailHistory, formatDate, DELIVERY_STATUS_CONFIG } from "../../services/MailingListService";

/**
 * ContactMailHistoryDropdown - Shows mail history when clicking on contact's mail count
 */
const ContactMailHistoryDropdown = ({ contactId, mailCount, deliveredCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Load history when dropdown opens
  useEffect(() => {
    if (isOpen && !history && !isLoading) {
      loadHistory();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const loadHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchContactMailHistory(contactId);
      setHistory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Get status icon
  const StatusIcon = ({ status }) => {
    const config = {
      sent: { icon: Mail, color: "text-blue-500" },
      pending: { icon: Clock, color: "text-slate-400" },
      in_transit: { icon: Truck, color: "text-amber-500" },
      in_local_area: { icon: MapPin, color: "text-purple-500" },
      delivered: { icon: CheckCircle, color: "text-green-500" },
      returned: { icon: RotateCcw, color: "text-red-500" }
    };
    const { icon: Icon, color } = config[status] || config.pending;
    return <Icon className={`w-4 h-4 ${color}`} />;
  };

  // Don't show if no mail has been sent
  if (mailCount === 0) {
    return (
      <span className="text-sm text-slate-400 dark:text-slate-500">
        No mail sent
      </span>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1 text-sm font-medium rounded-lg transition-colors hover:bg-sky-50 dark:hover:bg-sky-900/20 text-sky-600 dark:text-sky-400"
      >
        <Mail className="w-4 h-4" />
        <span>{mailCount}</span>
        {deliveredCount > 0 && (
          <span className="text-green-600 dark:text-green-400">
            ({deliveredCount} delivered)
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 right-0 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                <Mail className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <h4 className="font-semibold dark:text-white text-slate-900">
                  Mail History
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {mailCount} postcard{mailCount !== 1 ? "s" : ""} sent
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-sky-600 animate-spin" />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-4 text-center">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={loadHistory}
                  className="mt-2 text-sm text-sky-600 dark:text-sky-400 hover:underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* History items */}
            {history && history.history.length > 0 && (
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {history.history.map((item) => (
                  <div key={item.id} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <div className="flex items-start gap-3">
                      <StatusIcon status={item.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h5 className="font-medium text-sm dark:text-white text-slate-900 truncate">
                            {item.campaign_name}
                          </h5>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                            DELIVERY_STATUS_CONFIG[item.status]?.color === "green"
                              ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                              : DELIVERY_STATUS_CONFIG[item.status]?.color === "amber"
                              ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                              : DELIVERY_STATUS_CONFIG[item.status]?.color === "red"
                              ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                          }`}>
                            {DELIVERY_STATUS_CONFIG[item.status]?.label || item.status}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Sent {formatDate(item.sent_at)}
                          </span>
                          {item.expected_delivery_date && item.status !== "delivered" && (
                            <span>
                              Expected: {formatDate(item.expected_delivery_date)}
                            </span>
                          )}
                          {item.delivered_at && (
                            <span className="text-green-600 dark:text-green-400">
                              Delivered {formatDate(item.delivered_at)}
                            </span>
                          )}
                        </div>

                        {/* Tracking events */}
                        {item.tracking_events && item.tracking_events.length > 0 && (
                          <div className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                            <span className="font-medium">Last update:</span>{" "}
                            {item.tracking_events[item.tracking_events.length - 1].event?.replace("postcard.", "")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {history && history.history.length === 0 && (
              <div className="p-6 text-center">
                <Mail className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No mail history found
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMailHistoryDropdown;
